import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Table, Button, Input, Space, Tag, Popconfirm, message } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import AddModal from "./AddModal";
import Layout from '../../Layout'
import {
    Edit,
    X,
} from "lucide-react";
import { mixDate, mixDelV1, mixGetV1, mixPostV1, mixPutV1 } from "services/mixin";
import useAuthStore from "../../store/authStore";
import useTableColumnSettings, { SettingColumn } from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

const { Search } = Input;

interface TagItem {
    id: number;
    name: string;
    color: string;
    creator: {
        email: string;
        fullname: string;
    };
    description: string;
    created_at: string | null;
    updated_at: string | null;
}

export default function Profession() {
  const { t } = useTranslation();
  // Phân quyền thao tác: chỉ có quyền xem thì không hiện nút thêm/sửa/xóa
  const { isSuperAdmin, hasPermission } = useAuthStore();
  const canCreate = isSuperAdmin || hasPermission("profession.create");
  const canEdit = isSuperAdmin || hasPermission("profession.edit");
  const canDelete = isSuperAdmin || hasPermission("profession.delete");
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState<"add" | "edit">("add");
    const [editingItem, setEditingItem] = useState<TagItem | null>(null);

    const [data, setData] = useState<TagItem[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sortParam, setSortParam] = useState<string>("id,desc");

    const [actionModal, setActionModal] = useState<{ open: boolean; type: "approve" | "reject" | null; id: number | null }>({ open: false, type: null, id: null });

    const openDeleteModal = (id: number) => {
        setActionModal({
            open: true,
            type: "reject",
            id,
        });
    };

    const handleCloseActionModal = () => {
        setActionModal({
            open: false,
            type: null,
            id: null,
        });
    };

    const handleConfirmAction = async () => {
        if (actionModal.id !== null) {
            const res = await mixDelV1(`/api/v1/be/customer-occupation/${actionModal.id}`, {_method: "DELETE",})
            
            if (res.status) {
                message.success(t('profession.delete_success'));
                getList()
            } else {
                message.error(t('profession.delete_failed'));
            }
        }

        handleCloseActionModal();
    };

    const openAdd = () => {
        setMode("add");
        setEditingItem(null);
        setModalOpen(true);
    };

    const openEdit = (record: TagItem) => {
        setMode("edit");
        setEditingItem(record);
        setModalOpen(true);
    };

    const handleSubmit = async (values: any) => {

        const param = {...values}

        if (mode === "add") {
            const res = await mixPostV1('/api/v1/be/customer-occupation', param)

            if (res.status) {
                message.success(t('profession.add_success'));
                getList()
            } else {
                message.error(t('profession.add_failed'));
            }
        } else if (mode === "edit" && editingItem) {
           const res = await mixPutV1(`/api/v1/be/customer-occupation/${editingItem.id}`, param)
           
            if (res.status) {
                message.success(t('profession.edit_success'));
                getList()
            } else {
                message.error(t('profession.edit_failed'));
            }
        }

        setModalOpen(false);
    };

    const columns: SettingColumn<TagItem>[] = [
        {
            title: "Id",
            dataIndex: "id",
            key: "id",
            sorter: true,
        },
        {
            title: t('profession.col_name'),
            dataIndex: "name",
            key: "name",
            sorter: true,
        },
        {
            title: t('profession.col_description'),
            dataIndex: "description",
        },
        {
            title: t('profession.col_created_at'),
            key: "created_at",
            sorter: true,
            render: (_, record) => mixDate(record.created_at, {hour: true}),
        },
        {
            title: t('profession.col_updated_at'),
            key: "updated_at",
            sorter: true,
           render: (_, record) => mixDate(record.updated_at, {hour: true}),
        },
        {
            key: "actions",
            title: t('profession.col_action'),
            fixed: "right",
            lockVisible: true,
            render: (_, record) => (
                <div className="flex items-center gap-3">
                    <div className="flex items-center space-x-3 gap-2">
                        {canEdit && (
                            <button
                                onClick={() => openEdit(record)}
                                className="!text-[#3e91ab] hover:text-yellow-700 bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 p-1.5 rounded-md transition-colors"
                                title={t('profession.btn_edit')}
                            >
                                <Edit className="h-4 w-4" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => openDeleteModal(record.id)}
                                className="!text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 p-1.5 rounded-md transition-colors"
                                title={t('profession.btn_delete')}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "profession_list",
        (canEdit || canDelete) ? columns : columns.filter((c) => c.key !== "actions")
    );

    // ---------------  API------------------------
    const getList = async () => {
        setLoading(true);

         const param = {
            page,
            search: searchTerm,
            sort: sortParam,
        }

        const res = await mixGetV1('/api/v1/be/customer-occupations', param)

        if (res.status) {
            setData(res.data.items)
            setTotal(res.data.total)
        }
        setLoading(false);
    }

    useEffect(() => {
        getList()
    },[page, searchTerm, sortParam])
    

    return (
        <Layout>
            <Helmet>
                <title>{t('page.professions')} | Khothi</title>
            </Helmet>
            <div className="">
                <div className="flex gap-2 flex-col md:flex-row md:justify-between mb-4">
                    <h2 className="text-lg text-gray-900 font-semibold t-text-white">
                        {t('profession.title')}
                    </h2>

                    <Space>
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-search h-5 w-5 text-gray-400" aria-hidden="true"><path d="m21 21-4.34-4.34"></path><circle cx="11" cy="11" r="8"></circle></svg>
                            </div>
                            <input
                                type="text"
                                onChange={(e) => {
                                    setSearchTerm(e.target.value)
                                    setPage(1)
                                }}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 t-text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#3e91ab] focus:border-[#3e91ab] sm:text-sm transition-colors h-[32px]"
                                placeholder={t('profession.search_placeholder')}
                            />
                        </div>
                        {canCreate && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openAdd}
                                className="!bg-[#3e91ab] hover:!bg-[#357f96] border-none"
                            >
                                {t('profession.add_new')}
                            </Button>
                        )}
                        <ColumnSettingButton {...columnSettingProps} />
                    </Space>
                </div>

                <Table
                    loading={loading}
                    rowKey="id"
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={data}
                    scroll={{ x: scrollX }}
                    className="custom-ant-table"
                    onChange={(_pagination, _filters, sorter: any, extra: any) => {
                        // onChange của Table bắn cho cả phân trang lẫn sắp xếp. Nếu không lọc
                        // theo action thì bấm sang trang 2 sẽ bị chính handler này kéo về trang 1.
                        if (extra?.action !== 'sort') return;
                        if (sorter?.field && sorter?.order) {
                            const direction = sorter.order === 'ascend' ? 'asc' : 'desc';
                            setSortParam(`${sorter.field},${direction}`);
                        } else if (sorter?.columnKey && sorter?.order) {
                            const direction = sorter.order === 'ascend' ? 'asc' : 'desc';
                            setSortParam(`${sorter.columnKey},${direction}`);
                        } else {
                            setSortParam('id,desc');
                        }
                        setPage(1);
                    }}
                    pagination={{
                        current: page,
                        total: total,
                        pageSize: 10,
                        onChange: (p) => setPage(p),
                    }}
                />

                <AddModal
                    open={modalOpen}
                    mode={mode}
                    initialValues={
                        editingItem
                            ? {
                                name: editingItem.name,
                                description: editingItem.description,
                            }
                            : undefined
                    }
                    onCancel={() => setModalOpen(false)}
                    onSubmit={handleSubmit}
                />
                {/* Action Modal */}
                {actionModal.open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-gray-800 rounded-md shadow-xl w-full max-w-md">
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-bold text-gray-900 t-text-white">
                                    {t('profession.delete_title')}
                                </h3>
                                <p className="text-sm text-gray-500 text-000000e0 mt-1">
                                    {t('profession.delete_confirm')}
                                </p>
                            </div>
                            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                                <button
                                    onClick={handleCloseActionModal}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg !text-gray-700 dark:!text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                >
                                    {t('profession.cancel')}
                                </button>
                                <button
                                    onClick={handleConfirmAction}
                                    className={`px-4 py-2 !text-white rounded-lg transition ${actionModal.type === 'approve'
                                        ? 'bg-emerald-600 hover:bg-emerald-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                        }`}
                                >
                                    {t('profession.delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}